import { Injectable, inject } from '@angular/core';
import { HdIChingAccessService } from './hd-iching-access.service';
import { ICHING_ASSET_ROOT } from './iching.metadata';
import {
  IChingGeneral,
  IChingHexagram,
  IChingHexagramCommentary,
  IChingIndexEntry,
  IChingMethodContent,
  IChingSourceId,
} from './core/iching.models';

/** Talen waarvoor methode-content bestaat; andere talen vallen terug op 'en'. */
const METHOD_LANGUAGES: readonly string[] = ['nl', 'en'];

/**
 * Laadt Classic I Ching-content on demand uit de assets (native fetch, zoals de
 * Line Companion-service: geen auth-interceptors op statische bestanden).
 * Succesvolle loads worden per URL (bron+bestand) gecachet, mislukte niet.
 */
@Injectable({ providedIn: 'root' })
export class HdIChingContentService {
  private readonly access = inject(HdIChingAccessService);
  private readonly cache = new Map<string, Promise<unknown>>();

  /** Overschrijfbaar in specs. */
  protected fetchJson = async <T>(url: string): Promise<T> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Classic I Ching asset ${url} gaf ${response.status}`);
    }
    return (await response.json()) as T;
  };

  /** Bron voor een content-taal: NL krijgt Wilhelm alleen met de pack-feature. */
  sourceFor(lang: string): IChingSourceId {
    return lang === 'nl' && this.access.wilhelmNlAvailable() ? 'wilhelm-nl' : 'legge-en';
  }

  loadHexagram(number: number, source: IChingSourceId): Promise<IChingHexagram> {
    return this.cached(`${ICHING_ASSET_ROOT}/${source}/hexagram-${pad(number)}.json`);
  }

  loadCommentary(number: number, source: IChingSourceId): Promise<IChingHexagramCommentary> {
    return this.cached(`${ICHING_ASSET_ROOT}/${source}/hexagram-${pad(number)}.commentary.json`);
  }

  loadIndex(source: IChingSourceId): Promise<IChingIndexEntry[]> {
    return this.cached(`${ICHING_ASSET_ROOT}/${source}/index.json`);
  }

  loadGeneral(source: IChingSourceId): Promise<IChingGeneral> {
    return this.cached(`${ICHING_ASSET_ROOT}/${source}/general.json`);
  }

  async loadMethod(lang: string): Promise<IChingMethodContent> {
    const methodLang = METHOD_LANGUAGES.includes(lang) ? lang : 'en';
    const root = `${ICHING_ASSET_ROOT}/method/${methodLang}`;
    const [steps, trigrams, linePositions, terms] = await Promise.all([
      this.cached<IChingMethodContent['steps']>(`${root}/steps.json`),
      this.cached<IChingMethodContent['trigrams']>(`${root}/trigrams.json`),
      this.cached<IChingMethodContent['linePositions']>(`${root}/line-positions.json`),
      this.cached<IChingMethodContent['terms']>(`${root}/terms.json`),
    ]);
    return { steps, trigrams, linePositions, terms };
  }

  private cached<T>(url: string): Promise<T> {
    const hit = this.cache.get(url) as Promise<T> | undefined;
    if (hit) {
      return hit;
    }
    const promise = this.fetchJson<T>(url).catch((error: unknown) => {
      this.cache.delete(url);
      throw error;
    });
    this.cache.set(url, promise);
    return promise;
  }
}

function pad(number: number): string {
  return String(number).padStart(2, '0');
}
